import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import InputField from "../../../ui/components/InputField/InputField";
import SelectField from "../../../ui/components/SelectField/SelectField";
import { DIAL_CODES } from "../../../ui/data/dialCodes";
import BookingNationalInputs from "../../../booking/components/BookingNationalInputs";
import Department from "../../types/Department";

type Props = {
    department?: Department;
    profile?: any;
};

const TransferRequestPersonalsDetails: React.FC<Props> = ({ department, profile }) => {
    const t = useTranslate();
    const { control, formState: { errors }, register, watch, setValue } = useFormContext();

    return (
        <div className="space-y-6 mt-8">
            {/* --- PERSONAL DETAILS HEADER --- */}
            <div className="pb-4 border-b border-neutral-200 dark:border-neutral-700">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    {t("transfers.fields.personalDetails")}
                </h3>
            </div>

            {/* --- PASSENGER INFORMATION --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Controller
                    name="passengerName"
                    control={control}
                    rules={{ required: t("common.feedback.field_required") }}
                    render={({ field }) => (
                        <InputField
                            {...field}
                            label={t("transfers.fields.passengerName") || "First Name"}
                            type="text"
                            error={errors.passengerName?.message}
                        />
                    )}
                />
                <Controller
                    name="passengerLastName"
                    control={control}
                    rules={{ required: t("common.feedback.field_required") }}
                    render={({ field }) => (
                        <InputField
                            {...field}
                            label={t("transfers.fields.passengerLastName") || "Last Name"}
                            type="text"
                            error={errors.passengerLastName?.message}
                        />
                    )}
                />
                <Controller
                    name="passengerContactEmail"
                    control={control}
                    rules={{
                        required: t("common.feedback.field_required"),
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: t("booking.email") || "Invalid email",
                        },
                    }}
                    render={({ field }) => (
                        <InputField
                            {...field}
                            label={t("transfers.fields.passengerContactEmail") || "Email"}
                            type="email"
                            error={errors.passengerContactEmail?.message}
                        />
                    )}
                />
            </div>

            {/* --- PHONE NUMBER --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                    <Controller
                        name="dialCode"
                        control={control}
                        rules={{ required: t("common.feedback.field_required") }}
                        render={({ field }) => (
                            <SelectField
                                {...field}
                                label={t("transfers.fields.dialCode") || "Country Code"}
                                error={errors.dialCode?.message}
                            >
                                <option value=""></option>
                                {DIAL_CODES.map((dialCode) => (
                                    <option
                                        key={`${dialCode.code}-${dialCode.callingCode}`}
                                        value={`${dialCode.code}-${dialCode.callingCode}`}
                                    >
                                        {dialCode.code} +{dialCode.callingCode}
                                    </option>
                                ))}
                            </SelectField>
                        )}
                    />
                </div>
                <div className="md:col-span-2">
                    <Controller
                        name="passengerPhoneNumber"
                        control={control}
                        rules={{
                            required: t("common.feedback.field_required"),
                            pattern: {
                                value: /^[0-9]{6,13}$/,
                                message: t("booking.invalidTelephone") || "Invalid phone number",
                            },
                        }}
                        render={({ field }) => (
                            <InputField
                                {...field}
                                label={t("transfers.fields.passengerPhoneNumber") || "Phone Number"}
                                type="tel"
                                error={errors.passengerPhoneNumber?.message}
                            />
                        )}
                    />
                </div>
            </div>

            {/* --- INVOICE INFORMATION (for individual departments) --- */}
            {(department?.name?.toLowerCase() === "individual" || department?.name?.toLowerCase() === "individuals") && (
                <>
                    <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
                        <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                            {t("booking.invoiceInformation") || "Invoice Information"}
                        </h4>
                    </div>

                    {/* --- NATIONALITY AND DOCUMENT (usando BookingNationalInputs) --- */}
                    <BookingNationalInputs
                        t={t}
                        register={register}
                        errors={errors}
                        watch={watch}
                        setValue={setValue}
                        profile={profile}
                        control={control}
                    />

                    {/* --- INVOICE FULL NAME --- */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                            name="invoiceFullName"
                            control={control}
                            rules={{ required: t("common.feedback.field_required") }}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    label={t("booking.invoiceNameAndSurname") || "Full Name"}
                                    type="text"
                                    error={errors.invoiceFullName?.message}
                                />
                            )}
                        />

                        {/* --- INVOICE ADDRESS --- */}
                        <Controller
                            name="invoiceAddress"
                            control={control}
                            rules={{ required: t("common.feedback.field_required") }}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    label={t("booking.invoiceAddress") || "Address"}
                                    type="text"
                                    error={errors.invoiceAddress?.message}
                                />
                            )}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default TransferRequestPersonalsDetails;
